import { useCallback, useMemo, useRef, useState } from 'react';
import * as S from './styles';
import { colors } from '@/constants';
import { BarCode } from '..';
import { useDbFunctions, useEvents } from '@/talons';
const SearchSVG = `${process.env.PUBLIC_URL}/fi_search.svg`;
const SearchFillSVG = `${process.env.PUBLIC_URL}/fi_search_fill.svg`;
interface Props {
  id: string;
  value: string;
  placeholder?: string;
  onFocus?: (s?: any) => void;
  setInput: (s: any) => void;
  showCamera?: boolean;
  totalProducts?: number;
  showMicrophone?: boolean;
  backgroundColor?: string;
  isExactSearch?: boolean;
  setIsExactSearch?: (s: boolean) => void;
}

const PLACEHOLDER_LIST = [
  'Café',
  'Cerveja',
  'Leite',
  'Leite Condensado',
  'Detergente',
  'Achocolatado',
  'Esmalte',
  'Shampoo',
  'Condicionador',
  'Biscoito',
];

const Search = ({
  id,
  value,
  onFocus,
  isExactSearch,
  setIsExactSearch,
  setInput,
  backgroundColor,
  showCamera = true,
  totalProducts,
  showMicrophone = true,
  placeholder: propPlaceholder,
}: Props) => {
  const db = useDbFunctions();
  const { registerEvent } = useEvents(db);

  const placeholder = useMemo(() => {
    const firstProduct = PLACEHOLDER_LIST[Math.floor(Math.random() * PLACEHOLDER_LIST.length)];
    const secondProduct = PLACEHOLDER_LIST[Math.floor(Math.random() * PLACEHOLDER_LIST.length)];
    return propPlaceholder || `Busque por ${firstProduct}, ${secondProduct}...`;
  }, []);
  
  const win: any = window;
  const [isSpeaking, setIsSpeaking] = useState(false);

  const handleExactSearch = () => {
    setIsExactSearch?.(!isExactSearch)
    registerEvent({
      tipoEvento: 'SEARCH',
      obsEvento: `ExactSearch: ${!isExactSearch}`,
    });
  }

  const timeRef = useRef<NodeJS.Timeout | null>(null);

  const handleInputChange = (e: any, origin: string) => {
    setInput(e.target.value);
    if (timeRef.current) {
      clearTimeout(timeRef.current);
    }
    timeRef.current = setTimeout(() => {
      const totalProducts = document.getElementById('total-products')?.textContent;
      const labelTotal = totalProducts ? ` | TOTAL_PRODUTOS: ${totalProducts}` : '';
      registerEvent({
        tipoEvento: 'SEARCH',
        obsEvento: `InputChange: ${e.target.value} | ORIGEM: ${origin}${labelTotal}`,
      });

      window.clarity('set', 'busca_termo', (e.target.value).trim().toLowerCase());
      window.clarity('event', 'busca_realizada');

    }, 1000);

    return () => {
      if (timeRef.current) {
        clearTimeout(timeRef.current);
      }
      timeRef.current = null;
    };
  }

  const recognition = useMemo(() => {
    const Speech =
      win?.SpeechRecognition || win?.webkitSpeechRecognition || win?.mozSpeechRecognition || win?.msSpeechRecognition;
    const recognit = Speech ? new Speech() : null;
    if (recognit) {
      recognit.lang = 'pt-BR';

      recognit.onstart = () => {
        onFocus ? onFocus() : setIsSpeaking(true);
      };

      recognit.onresult = (event: any) => {
        const transcript = event.results?.[0]?.[0]?.transcript;
        handleInputChange({ target: { value: transcript } }, 'microphone');
      };

      recognit.onend = () => {
        setIsSpeaking(false);
      };
    }

    return recognit;
  }, [win, onFocus, setInput]);

  const speech = useCallback(() => {
    recognition?.start();
  }, [recognition]);

  return (
    <S.InputContent $backgroundColor={backgroundColor}>
      {!setIsExactSearch && <S.Search size={26} color={colors.gray.gray60} />}
      {setIsExactSearch && (
        <S.SearchContent onClick={handleExactSearch}>
          <S.EspecialSearchContent $isSpecialSearch={!!isExactSearch}>
            <S.SearchIcon src={isExactSearch ? SearchFillSVG : SearchSVG} alt="search" />
          </S.EspecialSearchContent>

          {isExactSearch && <S.EspecialSearchText>Exata</S.EspecialSearchText>}
        </S.SearchContent>
      )}

      <S.Input
        type="text"
        value={value}
        id="field-search"
        onFocus={onFocus}
        className="form-control"
        placeholder={placeholder}
        data-test={`component-search-input-${id}`}
        onChange={(e) => handleInputChange(e, 'input')}
      />

      {showCamera && <BarCode handleClick={onFocus} handleFilter={(value) => handleInputChange({ target: { value } }, 'camera')} />}

      {showMicrophone && (
        <S.Button>
          <S.OutlineMicrophone
            size={20}
            style={{ marginLeft: 5 }}
            color={isSpeaking ? colors.brand.red : colors.gray.gray60}
            onClick={() => (onFocus ? onFocus() : !isSpeaking && speech())}
          />
        </S.Button>
      )}
    </S.InputContent>
  );
};

export default Search;
